import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { EMPTY, Subject, catchError, filter, switchMap, take, takeUntil } from 'rxjs';
import { AuthService } from '../../../../services/auth.service';
import {
  CtaComparisonReportDto,
  CtaComparisonRowDto,
  MarketingAcquisitionService
} from '../../../../services/marketing-acquisition.service';
import { SelectInputComponent, SelectOption } from '../../../shared/select-input/select-input.component';
import type { AcquisitionDateRange } from '../acquisition.component';

type MetricKey = keyof CtaComparisonRowDto;
type MetricFormat = 'number' | 'currency' | 'roas' | 'days';

export interface CtaMetric {
  key: MetricKey;
  label: string;
  format: MetricFormat;
  /** Which direction wins; null for context-only rows (spend, volume). */
  better: 'lower' | 'higher' | null;
  /** Shown under the label. */
  note?: string;
  /** Percent field shown beside the value. */
  rateKey?: MetricKey;
}

interface CtaLoad {
  range: AcquisitionDateRange;
  campaignId: string;
}

/**
 * V2 — WhatsApp-CTA vs website-CTA Meta ads side by side. Leads aren't comparable (a WhatsApp lead
 * is a first message, a web lead has already signed in), so the winner is judged on cost per
 * sign-up, booking and first pickup.
 */
@Component({
  selector: 'app-cta-comparison',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectInputComponent],
  templateUrl: './cta-comparison.component.html'
})
export class CtaComparisonComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) range!: AcquisitionDateRange;

  readonly metrics: CtaMetric[] = [
    { key: 'adCount', label: 'Ads', format: 'number', better: null },
    { key: 'spend', label: 'Spend', format: 'currency', better: null },
    { key: 'clicks', label: 'Clicks', format: 'number', better: null },
    { key: 'leads', label: 'Leads', format: 'number', better: null, note: 'WhatsApp: first message. Web: signed in.' },
    { key: 'signUps', label: 'Sign-ups', format: 'number', better: null, rateKey: 'signUpRate' },
    { key: 'bookedCustomers', label: 'Booked', format: 'number', better: null, rateKey: 'bookingRate' },
    { key: 'firstPickups', label: 'First pickups', format: 'number', better: null },
    { key: 'revenue', label: 'Revenue', format: 'currency', better: null },
    { key: 'costPerLead', label: 'Cost / lead', format: 'currency', better: null, note: 'Not like-for-like across CTAs.' },
    { key: 'costPerSignUp', label: 'Cost / sign-up', format: 'currency', better: 'lower' },
    { key: 'costPerBookedCustomer', label: 'Cost / booked customer', format: 'currency', better: 'lower' },
    { key: 'cac', label: 'CAC', format: 'currency', better: 'lower', note: 'Spend ÷ first pickups.' },
    { key: 'revenueRoas', label: 'Revenue ROAS', format: 'roas', better: 'higher' },
    { key: 'gmvRoas', label: 'GMV ROAS', format: 'roas', better: null },
    { key: 'medianDaysToFirstPickup', label: 'Days to first pickup', format: 'days', better: 'lower', note: 'Median, from first touch.' }
  ];

  campaignId = '';
  campaignOptions: SelectOption[] = [{ value: '', label: 'All campaigns' }];

  loading = false;
  errorMessage = '';
  report: CtaComparisonReportDto | null = null;

  private readonly load$ = new Subject<CtaLoad>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private acquisitionService: MarketingAcquisitionService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.load$
      .pipe(
        switchMap(({ range, campaignId }) => {
          this.loading = true;
          this.errorMessage = '';
          return this.acquisitionService.getCtaComparison(range.from, range.to, campaignId).pipe(
            catchError(error => {
              this.onError(error);
              return EMPTY;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(report => this.onReport(report));

    this.authService.getMsalInitializedObservable()
      .pipe(filter(initialized => initialized), take(1), takeUntil(this.destroy$))
      .subscribe(() => this.reload());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['range'] && !changes['range'].firstChange) {
      this.reload();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reload(): void {
    this.load$.next({ range: this.range, campaignId: this.campaignId });
  }

  get whatsApp(): CtaComparisonRowDto | undefined {
    return this.report?.rows.find(r => r.cta === 'whatsapp');
  }

  get web(): CtaComparisonRowDto | undefined {
    return this.report?.rows.find(r => r.cta === 'web');
  }

  get unknown(): CtaComparisonRowDto | undefined {
    return this.report?.rows.find(r => r.cta === 'unknown');
  }

  value(row: CtaComparisonRowDto | undefined, key: MetricKey): number | null {
    const value = row?.[key];
    return typeof value === 'number' ? value : null;
  }

  /** The CTA that wins this metric, or null when it isn't judged, a side is missing, or it's a tie. */
  winner(metric: CtaMetric): 'whatsapp' | 'web' | null {
    const whatsApp = this.value(this.whatsApp, metric.key);
    const web = this.value(this.web, metric.key);
    if (!metric.better || whatsApp == null || web == null || whatsApp === web) {
      return null;
    }
    const whatsAppWins = metric.better === 'lower' ? whatsApp < web : whatsApp > web;
    return whatsAppWins ? 'whatsapp' : 'web';
  }

  private onReport(report: CtaComparisonReportDto): void {
    this.report = report;
    this.campaignOptions = [
      { value: '', label: 'All campaigns' },
      ...report.campaigns.map(c => ({
        value: c.id,
        label: `${c.name || c.id}${c.hasBothCtas ? ' (both CTAs)' : ''}`
      }))
    ];
    // A campaign that no longer appears in the new range falls back to all campaigns.
    if (this.campaignId && !report.campaigns.some(c => c.id === this.campaignId)) {
      this.campaignId = '';
      this.reload();
      return;
    }
    this.loading = false;
  }

  private onError(error: { status?: number; error?: { error?: string } }): void {
    this.loading = false;
    this.report = null;
    if (error.status === 400 && error.error?.error) {
      this.errorMessage = error.error.error;
    } else if (error.status === 403) {
      this.errorMessage = 'Access denied. You do not have permission to view marketing reports.';
    } else {
      this.errorMessage = 'Failed to load the CTA comparison. Please try again.';
    }
  }
}
